import React from 'react';
import {
  Box,
  Heading,
  Text,
  Grid,
  GridItem,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  HStack,
  Badge,
  Card,
  CardHeader,
  CardBody,
} from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';

const Cms1500Replica = ({ results, claimsData, claimType }) => {
  if (!results) return null;

  // --- Box 21 Logic ---
  const slots = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
  const diagMap = {};
  
  if (results.ordered_diagnoses) {
    results.ordered_diagnoses.forEach(dx => {
      if (dx.pointer) {
        diagMap[dx.pointer] = dx;
      }
    });
  }

  // --- Box 24 Logic ---
  // Use detailed claims data if available, otherwise fallback to basic context strings
  let serviceLines = [];
  if (claimsData && claimsData[claimType] && claimsData[claimType].cpt_codes) {
    serviceLines = claimsData[claimType].cpt_codes.map(codeObj => {
      let modString = '';
      if (codeObj.modifiers && Array.isArray(codeObj.modifiers) && codeObj.modifiers.length > 0) {
        modString = ` | ${codeObj.modifiers.join(', ')}`;
      } else if (codeObj.modifier) {
        modString = ` | ${codeObj.modifier}`;
      }
      
      return {
        cpt: codeObj.cpt_code,
        display: `${codeObj.cpt_code}${modString}`,
        units: codeObj.units || 1
      };
    });
  } else {
    const cpts = results.context?.cpt_codes || [];
    serviceLines = cpts.map(cpt => ({
      cpt: cpt,
      display: cpt,
      units: 1
    }));
  }

  const pointersDict = results.line_level_pointers || {};
  const serviceDate = results.service_date || '';

  const getPointersForCpt = (cpt) => {
    const baseCpt = cpt.split('-')[0].trim();
    if (pointersDict[baseCpt]) {
      return pointersDict[baseCpt].join('');
    }
    if (pointersDict['ALL_CPTS']) {
      return pointersDict['ALL_CPTS'].join('');
    }
    return '';
  };

  return (
    <VStack spacing={6} align="stretch" w="100%">
      {/* Box 21: Diagnosis */}
      <Card variant="outline" borderColor="gray.300">
        <CardHeader bg="gray.50" py={3} borderBottomWidth="1px" borderColor="gray.200">
          <Heading size="sm" color="gray.700">21. DIAGNOSIS OR NATURE OF ILLNESS OR INJURY</Heading>
        </CardHeader>
        <CardBody p={0}>
          <Grid templateColumns="repeat(4, 1fr)" gap={0}>
            {slots.map((slot, index) => {
              const dx = diagMap[slot];
              // In a 4-column grid, the rightmost column is index 3, 7, 11
              const isRightmost = (index + 1) % 4 === 0;
              return (
                <GridItem 
                  key={slot} 
                  borderBottomWidth="1px" 
                  borderRightWidth={isRightmost ? "0" : "1px"} 
                  borderColor="gray.200"
                  bg={dx?.needs_review ? "yellow.50" : "white"}
                  p={2}
                >
                  <HStack align="start" spacing={2}>
                    <Text fontWeight="bold" color="gray.500" fontSize="sm" w="20px">{slot}.</Text>
                    {dx ? (
                      <Box>
                        <Text fontWeight="bold" fontSize="sm">
                          {dx.code}
                          {dx.needs_review && (
                            <WarningIcon color="yellow.500" ml={2} boxSize={3} title="Requires review" />
                          )}
                        </Text>
                        <Text fontSize="xs" color="gray.600" noOfLines={1} title={dx.description}>
                          {dx.description}
                        </Text>
                      </Box>
                    ) : (
                      <Text color="gray.300" fontSize="sm" fontStyle="italic">Empty</Text>
                    )}
                  </HStack>
                </GridItem>
              );
            })}
          </Grid>
        </CardBody>
      </Card>

      {/* Box 24: Service Lines */}
      <Card variant="outline" borderColor="gray.300">
        <CardHeader bg="gray.50" py={3} borderBottomWidth="1px" borderColor="gray.200">
          <Heading size="sm" color="gray.700">24. PROCEDURES, SERVICES, OR SUPPLIES</Heading>
        </CardHeader>
        <CardBody p={0} overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg="gray.100">
              <Tr>
                <Th>A. DATE(S)</Th>
                <Th>B. PLACE</Th>
                <Th>D. PROCEDURES (CPT | MOD)</Th>
                <Th>E. DIAG POINTER</Th>
                <Th>F. CHARGES</Th>
                <Th>G. UNITS</Th>
                <Th>J. PROVIDER</Th>
              </Tr>
            </Thead>
            <Tbody>
              {serviceLines.length > 0 ? (
                serviceLines.map((line, idx) => (
                  <Tr key={idx}>
                    <Td fontSize="sm">{serviceDate}</Td>
                    <Td fontSize="sm"></Td>
                    <Td fontSize="sm" fontWeight="medium">{line.display}</Td>
                    <Td fontSize="sm" fontWeight="bold">{getPointersForCpt(line.cpt)}</Td>
                    <Td fontSize="sm"></Td>
                    <Td fontSize="sm">{line.units}</Td>
                    <Td fontSize="sm"></Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={7} textAlign="center" color="gray.500" py={4}>
                    No CPT codes found in context.
                  </Td>
                </Tr>
              )}
              {/* Pad with empty rows to make it look like a form if there are few CPTs */}
              {Array.from({ length: Math.max(0, 6 - serviceLines.length) }).map((_, idx) => (
                <Tr key={`empty-${idx}`}>
                  <Td py={4}></Td>
                  <Td></Td>
                  <Td></Td>
                  <Td></Td>
                  <Td></Td>
                  <Td></Td>
                  <Td></Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default Cms1500Replica;
